/*
 * Copyright (C) 2018 Bruno López Trigo
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.

 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.

 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 */

import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
    TextField,
    FormControlLabel,
    Checkbox,
    Tooltip,
    Paper,
    Typography,
    Button,
    CircularProgress
} from "@material-ui/core";
import CustomizedSnackbars from "../global/CustomizedSnackbars";
import { Redirect } from "react-router-dom";

const styles = theme => ({
    root: {
        color: "#4caf50",
        "&$checked": {
            color: "#4caf50"
        }
    },
    checked: {},
    container: {
        marginBottom:100,
        paddingTop: 100,
        paddingBottom: 20,
        paddingLeft: 20,
        paddingRight: 20,
        margin: "auto",
        width: "90%",
        textAlign: "center"
    },
    textField: {
        width: "100%"
    },
    textFieldName: {
        width: "100%",
        marginTop: 20
    },
    booleanBar: {
        display: "-webkit-flex",
        webkitFlexWrap: "wrap",
        flexWrap: "wrap",
        justifyContent: "space-around"
    },
    button: {
        width:"150px",
        marginTop: "30px",
        backgroundColor: "#4caf50",
        fontSize: "20px",
        color: "white",
        "&:hover": {
            backgroundColor: "#357a38"
        }
    },
    buttonCancel: {
        width:"150px",
        marginTop: "30px",
        backgroundColor: "#ff5f52",
        fontSize: "20px",
        color: "white",
        "&:hover": {
            backgroundColor: "#C62828"
        }
    },
    icon: {
        marginLeft: 10
    },
    file: {
        marginTop: 20
    },
    progress: {
        margin: "auto",
        marginTop: 80,
        marginBottom: 30
    },
    loader: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        justifyItems: "center"
    }
});

class FormJ48 extends Component {
    constructor(props) {
        super(props);
        this.state = {
            algorithm: "J48",
            J48params: {
                binarySplits: false,
                collapseTree: true,
                doNotMakeSplit: false,
                confidenceFactor: 0.25,
                minNumObj: 2,
                numFolds: 3,
                reduceError: false,
                seed: 1,
                subtreeRaising: true,
                unpruned: false,
                useLaplace: false,
                useMDL: true
            },
            error: false
        };
        this.saveConfiguration = this.saveConfiguration.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.cancelOperation = this.cancelOperation.bind(this);
    }

    handleChange = name => event => {
        var regex = /^[A-Za-z0-9]{4,20}$/;
        this.setState({
            nameError: event.target.value.match(regex) ? false : true,
            [name]: event.target.value
        });
    };

    handleChangeParam = name => event => {
        var regex = /^-?[0-9]+\.?[0-9]*$/;
        var params = this.state.J48params;
        params[name] = event.target.value;
        this.setState({
            J48params: params,
            [name]: event.target.value.match(regex) ? false : true
        });
    };

    handleChangeBoolean = name => event => {
        var params = this.state.J48params;
        params[name] = event.target.checked;
        this.setState({ J48params: params });
    };

    changeFile(event) {
        this.setState({
            file: event.target.files[0]
        });
    }

    handleClose = (event, reason) => {
        this.setState({
            error: false
        });
    };

    async saveConfiguration() {
        this.props.saveConfigurationJ48(this.state.J48params);
    }
    async cancelOperation() {
        this.props.exitConfigurationJ48();
    }

    render() {
        const { classes } = this.props;

        return this.state.loading ? (
            <div className={classes.loader}>
                <CircularProgress className={classes.progress} size={100} />
                <Typography variant="h3" align="center">
                    {this.props.language.labels[this.props.language.id].loading}
                </Typography>
            </div>
        ) : (
            <Fragment>
                <Paper className={classes.container}>
                    <Typography variant="h4">
                        {this.props.language.labels[this.props.language.id].build +
                        " " +
                        this.state.algorithm}
                    </Typography>
                    <form>
                        <Tooltip
                            title="The confidence factor used for pruning (smaller values incur more pruning)"
                            placement="top"
                        >
                            <TextField
                                id="standard-name"
                                label="confidenceFactor"
                                className={classes.textField}
                                value={this.state.J48params.confidenceFactor}
                                onChange={this.handleChangeParam("confidenceFactor")}
                                margin="normal"
                                error={this.state.confidenceFactor}
                            />
                        </Tooltip>
                        <Tooltip
                            title="The minimum number of instances per leaf"
                            placement="top"
                        >
                            <TextField
                                id="standard-name"
                                label="minNumObj"
                                className={classes.textField}
                                value={this.state.J48params.minNumObj}
                                onChange={this.handleChangeParam("minNumObj")}
                                margin="normal"
                                error={this.state.minNumObj}
                            />
                        </Tooltip>

                        <Tooltip
                            title="Determines the amount of data used for reduced-error pruning.  One fold is used for pruning, the rest for growing the tree"
                            placement="top"
                        >
                            <TextField
                                id="standard-name"
                                label="numFolds"
                                className={classes.textField}
                                value={this.state.J48params.numFolds}
                                onChange={this.handleChangeParam("numFolds")}
                                margin="normal"
                                error={this.state.numFolds}
                            />
                        </Tooltip>
                        <Tooltip
                            title="The seed used for randomizing the data when reduced-error pruning is used"
                            placement="top"
                        >
                            <TextField
                                id="standard-name"
                                label="seed"
                                className={classes.textFieldName}
                                value={this.state.J48params.seed}
                                onChange={this.handleChangeParam("seed")}
                                margin="normal"
                                error={this.state.seed}
                            />
                        </Tooltip>
                        <div className={classes.booleanBar}>
                            <Tooltip
                                title="Whether to use binary splits on nominal attributes when building the trees"
                                placement="top"
                            >
                                <FormControlLabel
                                    className={classes.boolean}
                                    control={
                                        <Checkbox
                                            classes={{
                                                root: classes.root,
                                                checked: classes.checked
                                            }}
                                            checked={this.state.J48params.binarySplits}
                                            onChange={this.handleChangeBoolean("binarySplits")}
                                            value="binarySplits"
                                            color="primary"
                                        />
                                    }
                                    label="binarySplits"
                                />
                            </Tooltip>
                            <Tooltip
                                title="Whether parts are removed that do not reduce training error"
                                placement="top"
                            >
                                <FormControlLabel
                                    className={classes.boolean}
                                    control={
                                        <Checkbox
                                            classes={{
                                                root: classes.root,
                                                checked: classes.checked
                                            }}
                                            checked={this.state.J48params.collapseTree}
                                            onChange={this.handleChangeBoolean("collapseTree")}
                                            value="collapseTree"
                                            color="primary"
                                        />
                                    }
                                    label="collapseTree"
                                />
                            </Tooltip>
                            <Tooltip
                                title="If true, the split point is not relocated to an actual data value. This can yield substantial speed-ups for large datasets with numeric attributes"
                                placement="top"
                            >
                                <FormControlLabel
                                    className={classes.boolean}
                                    control={
                                        <Checkbox
                                            classes={{
                                                root: classes.root,
                                                checked: classes.checked
                                            }}
                                            checked={this.state.J48params.doNotMakeSplit}
                                            onChange={this.handleChangeBoolean("doNotMakeSplit")}
                                            value="doNotMakeSplit"
                                            color="primary"
                                        />
                                    }
                                    label="doNotMakeSplit"
                                />
                            </Tooltip>

                            <Tooltip
                                title="Whether reduced-error pruning is used instead of C.4.5 pruning"
                                placement="top"
                            >
                                <FormControlLabel
                                    className={classes.boolean}
                                    control={
                                        <Checkbox
                                            classes={{
                                                root: classes.root,
                                                checked: classes.checked
                                            }}
                                            checked={this.state.J48params.reduceError}
                                            onChange={this.handleChangeBoolean("reduceError")}
                                            value="reduceError"
                                            color="primary"
                                        />
                                    }
                                    label="reduceError"
                                />
                            </Tooltip>
                            <Tooltip
                                title="Whether to consider the subtree raising operation when pruning"
                                placement="top"
                            >
                                <FormControlLabel
                                    className={classes.boolean}
                                    control={
                                        <Checkbox
                                            classes={{
                                                root: classes.root,
                                                checked: classes.checked
                                            }}
                                            checked={this.state.J48params.subtreeRaising}
                                            onChange={this.handleChangeBoolean("subtreeRaising")}
                                            value="subtreeRaising"
                                            color="primary"
                                        />
                                    }
                                    label="subtreeRaising"
                                />
                            </Tooltip>
                            <Tooltip title="Whether pruning is performed" placement="top">
                                <FormControlLabel
                                    className={classes.boolean}
                                    control={
                                        <Checkbox
                                            classes={{
                                                root: classes.root,
                                                checked: classes.checked
                                            }}
                                            checked={this.state.J48params.unpruned}
                                            onChange={this.handleChangeBoolean("unpruned")}
                                            value="unpruned"
                                            color="primary"
                                        />
                                    }
                                    label="unpruned"
                                />
                            </Tooltip>
                            <Tooltip
                                title="Whether counts at leaves are smoothed based on Laplace"
                                placement="top"
                            >
                                <FormControlLabel
                                    className={classes.boolean}
                                    control={
                                        <Checkbox
                                            classes={{
                                                root: classes.root,
                                                checked: classes.checked
                                            }}
                                            checked={this.state.J48params.useLaplace}
                                            onChange={this.handleChangeBoolean("useLaplace")}
                                            value="useLaplace"
                                            color="primary"
                                        />
                                    }
                                    label="useLaplace"
                                />
                            </Tooltip>
                            <Tooltip
                                title="Whether MDL correction is used when finding splits on numeric attributes"
                                placement="top"
                            >
                                <FormControlLabel
                                    className={classes.boolean}
                                    control={
                                        <Checkbox
                                            classes={{
                                                root: classes.root,
                                                checked: classes.checked
                                            }}
                                            checked={this.state.J48params.useMDL}
                                            onChange={this.handleChangeBoolean("useMDL")}
                                            value="useMDL"
                                            color="primary"
                                        />
                                    }
                                    label="useMDL"
                                />
                            </Tooltip>
                        </div>
                        <Button
                            variant="contained"
                            className={classes.button}
                            onClick={this.saveConfiguration}
                        >
                            {this.props.language.labels[this.props.language.id].saveButton}
                        </Button>
                        &nbsp;
                        &nbsp;
                        &nbsp;
                        <Button
                            variant="contained"
                            className={classes.buttonCancel}
                            onClick={this.cancelOperation}
                        >
                            {this.props.language.labels[this.props.language.id].cancelButton}
                        </Button>

                    </form>
                </Paper>
                <CustomizedSnackbars
                    openFail={this.state.error}
                    handleClose={this.handleClose}
                    message={this.state.message}
                />
                {this.state.redirect && <Redirect to="/" />}
                {this.state.ready && <Redirect to="/datasets" />}
            </Fragment>
        );
    }
}

FormJ48.propTypes = {
    classes: PropTypes.object.isRequired
};

export default withStyles(styles)(FormJ48);
